import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  ChangeDetectionStrategy
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { EventItemService } from '@core/services/event-item.service';
import { faCalendar } from '@fortawesome/free-solid-svg-icons';
import { NgbCalendar, NgbDate, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { take } from 'rxjs';

@Component({
    selector: 'app-events-form',
    templateUrl: './events-form.component.html',
    styleUrls: ['./events-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EventsFormComponent implements OnInit, OnChanges {
  public minDate: NgbDateStruct;

  constructor(
    private fb: FormBuilder,
    private eventItem: EventItemService,
    private toastr: ToastrService,
    private calendar: NgbCalendar
  ) {
    this.minDate = this.calendar.getToday()
  }
  @Input() isCollapsed = false;
  @Input() eventData: any = {};
  @Output() refreshData: EventEmitter<any> = new EventEmitter<any>();

  isEdit = false;
  faCalendar = faCalendar;
  public selectedFile: any;
  public formData!: FormData;

  pageContent!: any | null;
  imageFile!: File;
  isProcessing = false;
  eventItemForm!: FormGroup | any;

  date!: { year: number; month: number };

  get title() {
    return this.eventItemForm.get('title');
  }
  get startDate() {
    return this.eventItemForm.get('startDate');
  }
  get endDate() {
    return this.eventItemForm.get('endDate');
  }
  get registrationLinkText() {
    return this.eventItemForm.get('registrationLinkText');
  }
  get registrationUrl() {
    return this.eventItemForm.get('registrationUrl');
  }
  get flyerUrl() {
    return this.eventItemForm.get('flyerUrl');
  }
  get location() {
    return this.eventItemForm.get('title');
  }
  get imageUrl() {
    return this.eventItemForm.get('imageUrl');
  }
  get imageFileName() {
    return this.eventItemForm.get('imageFileName');
  }

  ngOnInit() {
    this.initForm();
  }

  ngOnChanges(): void {
    if (this.eventData) {
      let startDate = new Date(this.eventData.startDate);
      let endDate = new Date(this.eventData.endDate);

      this.eventItemForm.patchValue({
        title: this.eventData.title,
        startDate: this.eventData.startDate
          ? new NgbDate(
              startDate.getFullYear(),
              startDate.getMonth() + 1,
              startDate.getDate()
            )
          : null,
        endDate: this.eventData.endDate
          ? new NgbDate(
              endDate.getFullYear(),
              endDate.getMonth() + 1,
              endDate.getDate()
            )
          : null,
        registrationLinkText: this.eventData.registrationLinkText,
        registrationUrl: this.eventData.registrationUrl,
        flyerUrl: this.eventData.flyerUrl,
        location: this.eventData.location,
        imageUrl: this.eventData.imageUrl,
        imageFileName: this.eventData.imageFileName,
      });
    }
  }

  initForm() {
    const urlRegex =
      '^(https?|ftp):\\/\\/(www\\.)?[\\w.-]+\\.[a-zA-Z]{2,}(\\/\\S*)?$';

    this.eventItemForm = this.fb.group({
      title: [null, [Validators.required, Validators.maxLength(50)]],
      startDate: [null, [Validators.required]],
      endDate: [null],
      registrationLinkText: [null],
      registrationUrl: [null, [Validators.required, Validators.pattern(urlRegex)]],
      flyerUrl: [null, Validators.pattern(urlRegex)],
      location: [null, Validators.required],
      imageUrl: [null, Validators.pattern(urlRegex)],
      imageFileName: [null, Validators.required],
    });
  }

  public fileChange(event: any) {
    if (!this.eventData) this.eventData = {};
    const fileList: FileList = event.target.files;
    if (fileList.length > 0) {
      const chosenFile: File = fileList[0];
      this.selectedFile = chosenFile;
      this.eventItemForm.get('imageFileName').markAsDirty();
      this.eventItemForm.patchValue({
        imageFileName: chosenFile.name,
      });
      this.eventData.imageFileName = chosenFile.name;
    }
  }

  public prepareData() {
    Object.assign(this.eventData, this.eventItemForm.value);
    this.formData = new FormData();
    if (this.selectedFile) {
      this.formData.append(
        'eventItemImage',
        this.selectedFile,
        this.selectedFile.name
      );
    }
    this.eventData.startDate = `${this.eventData.startDate.month}/${this.eventData.startDate.day}/${this.eventData.startDate.year}`;
    if (this.eventData.endDate)
      this.eventData.endDate = `${this.eventData.endDate.month}/${this.eventData.endDate.day}/${this.eventData.endDate.year}`;

    if (this.eventData.endDate) {
      const startDate = new Date(
        this.eventData.startDate + ' ' + this.eventData.startTime
      );
      const endDate = new Date(
        this.eventData.endDate + ' ' + this.eventData.endTime
      );

      if (startDate >= endDate) {
        this.toastr.error('End date should be after the start date.');
        return;
      }
    }
    this.formData.append('event', JSON.stringify(this.eventData));
  }

  saveForm() {
    if (this.eventItemForm.invalid) {
      this.toastr.error('Please fix your form errors!');
      return;
    }
    this.prepareData();
    this.isProcessing = true;
    this.eventItem
      .saveEvent(this.formData, 'Event-Item successfully saved!')
      .pipe(take(1))
      .subscribe((data: any) => {
        this.isProcessing = false;
        this.refreshData.emit(true);
      });
  }
}
